package com.pincer.app

import com.pincer.core.Logger

// A single copy of the translations will be included in the source of all applications.
// At some point we could make this a build parameter so that different versions could be
// shipped in different parts of the world with a corresponding choice of default.
// Changes to locale made through the controller overwrite these strings.

class I18n {

    companion object {

        val t = mutableMapOf<String,String>()

        fun hasTranslation(key: String): Boolean {
            return (t[key] != null)
        }

        fun translate(key: String, vararg params: String): String {
            var build = t[key] ?: key
            var count = 0
            for (param in params) {
                count++
                build = build.replace("%$count%", param)
            }
            return build
        }

        var loadedLocale = "en"

        fun defaults() {
            t["root"] = "Pincer"
            t["pid"] = "Reference"
            t["shareableLink"] = "Shareable Link"
            t["summaries"] = "Your Pincers"
            t["settings"] = "Settings"
            t["showAdvancedOptions"] = "Advanced Options"
            t["showAdvancedOptionsHelpText"] = "Choose this to display more advanced settings and features throughout the application."
            t["settings"] = "Settings"
            t["credlessWarning"] = "Use the form to tell us your email address or phone number to fully set up your account."
            t["yourName"] = "Your Name"
            t["pincerName"] = "Pincer Name"
            t["pincerDescription"] = "Pincer Description"
            t["pincerRules"] = "Base Rules"
            t["redRules"] = "Red Rules"
            t["greenRules"] = "Green Rules"
            t["rules"] = "Availability Rules"
            t["participantRules"] = "Availability Rules for this Participant"
            t["roles"] = "Participant Roles"
            t["locale"] = "Language"
            t["zone"] = "Time Zone"
            t["serverUrl"] = "Server"
            t["webSocketUrl"] = "WebSocket Server"
            t["problem"] = "Problem"
            t["badLocale"] = "Please enter a supported two letter language code, e.g. 'en' or 'es'."
            t["badServer"] = "Can't connect to the server. Please check the address."
            t["candidates"] = "Possibilities"
            t["chats"] = "Chat"
            t["name"] = "Name"
            t["wording"] = "Wording"
            t["participants"] = "Participants"
            t["newPincer"] = "New Pincer"
            t["termsAndConditions"] = "Terms and Conditions"
            t["termsSummary"] = "Click here to inidicate you have read and agree to the terms of the %1%."
            t["pincerCustomerAgreement"] = "Pincer Customer Agreement"
            t["acceptTerms"] = "Accept Terms and Conditions"
            t["allowedValues"] = "Possible Values"
            t["true"] = "True"
            t["false"] = "False"
            t["en-GB"] = "British English"
            t["en-US"] = "American English"
            t["es"] = "Spanish"
            t["fr"] = "French"
            t["resetDevice"] = "Reset Device"
            t["permissions"] = "Permissions"
            t["addChatMessage"] = "Add Message"
            t["draftChat"] = "Message"
            t["addChat"] = "Send"
            t["participantRules"] = "Your Rules"
            t["unexpectedError"] = "An unexpected error occurred"
            t["backgroundFailed"] = "An important background process failed"
            t["getStarted"] = "Get started ..."
            t["withEmailAddress"] = "with an email address"
            t["withGoogle"] = "with Google"
            t["withMicrosoft"] = "with Microsoft"
            t["withApple"] = "with Apple"
            t["withDeviceCode"] = "with a QR Code"
            t["submitForm"] = "Submit Form"
            t["next"] = "Next"
            t["loadPincer"] = "Load Pincer"
            t["loading"] = "Loading ..."
            t["questions"] = "Questions"
            t["newQuestion"] = "Create New Question"
            t["question-code"] = "Question Code"
            t["question-format"] = "Question Type"
            t["question-wording"] = "Wording of Question"
            t["question-help-text"] = "Help Text"
            t["question-options"] = "Options"
            t["question-roles"] = "Roles to Ask"
            t["question-regex"] = "Regular Expression"
            t["question-regex-help"] = "This is an advanced feature. You have been warned! Use this to specify a strict pattern or format that answers need to fit. Here's [a good place to learn about regular expressions](https://regexlearn.com/)"
            t["question-code-help"] = "Choose a simple label for you to manage the answers. It won't be shown to your users. Examples: FNAME, AGE, EMAIL, ZIP_CODE"
            t["question-options-help"] = "Enter each option on a different line. Try to keep them short."
            t["question-roles-help"] = "This is an advanced feature. Enter a comma separated list of role names."
            t["answers"] = "Answers"
            t["newPincerName"] = "New Pincer Name"
            t["createNewPincer"] = "Create New Pincer"
            t["takePart"] = "Take Part"
            t["SIMPLE"] = "One-line Text"
            t["MULTILINE"] = "Multi-line Text"
            t["DATE"] = "Date"
            t["TIME"] = "Time"
            t["DATETIME"] = "Date and Time"
            t["EMAIL"] = "Email Address"
            t["PHONE"] = "Phone Number"
            t["REGEX"] = "Text in a Specific Format"
            t["EMAIL"] = "Email Address"
            t["SELECT"] = "Select from List"
            t["CHECKBOX"] = "Checkbox"
            t["addQuestion"] = "Create New Question"
            t["invalid-missing"] = "Please enter something"
            t["invalid-too-short"] = "This text is too short"
            t["invalid-too-long"] = "This text is too long"
            t["invalid-problem-word"] = "This text contains a problem word or phrase"
            t["invalid-problem-character"] = "This text contains a problem character"
            t["invalid-format"] = "Please use the correct format"
            t["invalid-question-code"] = "Please use just captial letters and underscores."
            t["invalid-not-enough-options"] = "Please enter at least two options."
            t["invalid-rules"] = "Something looks wrong with these rules"
            t["invalid-base-rules"] = "Sorry, base rules can't include any negation ('!' character)"
            t["invalid-zalgo"] = "There's too many combined characters"
            t["invalid-angle-bracket"] = "Sorry, you can't include the '<' symbol"
            t["invalid-email"] = "Please enter a valid email address"
            t["invalid-phone"] = "Please enter a valid phone number"
            t["invalid-code"] = "Please enter the six digit number"
            t["invalid-regex"] = "Please enter a valid 'regular expression'"
            t["invalid-role"] = "Please use one word of only lowercase letters"
            t["invalid-roles"] = "Please enter a comma separated list of single words, each using only lowercase letters"
            t["suggestions"] = "Suggested Times"
            t["top-candidate"] = "Current Best Option:"
            t["someone"] = "Someone"
            t["candidate-minutes"] = "%1% minutes"
            t["candidate-available"] = "Can make it: %1%"
            t["candidate-undecided"] = "Not decided yet: %1%"
            t["answer-format-DATE"] = "A date, like 2026-10-31"
            t["answer-format-TIME"] = "A time, like 14:30"
            t["answer-format-DATETIME"] = "A date and time, like 2026-10-31T14:30"
            t["duration"] = "Duration"
            t["days"] = "Days"
            t["emailOrPhone"] = "Your Email or Phone Number"
            t["credentialHelpText"] = "Optional, but this gives you control over all your pincers across different devices."
            t["invalid-credential"] = "Please enter a valid email address or phone number"
            t["emailAddress"] = "Email Address"
            t["credentialCode"] = "Access Code"
            t["credentialCodeHelpText"] = "We've just sent an email to %1% with a numeric code"
            t["sixDigitCode"] = "Enter the six digit code"
            t["lockedPrincipal"] = "That account is locked"
            t["wrongCode"] = "Oopse, that code might be wrong"
            t["tryAgainIn"] = "Please try again in %1% minutes."
            t["account"] = "Account"
            t["logoutThisDevice"] = "Logout on this device"
            t["logoutAllDevices"] = "Logout on all devices"
            t["useOpenRole"] = "Open for Attendees"
            t["useOpenRoleHelpText"] = "If you check this option, anyone who knows the link will be allowed to attend."
            t["openRole"] = "Open Role"
            t["openRoleHelpText"] = "When people join through an open link they get added in this role. If you change this from the default ('attendee') you might need to change the permissions too."
            t["makeTemplate"] = "Make this a Template"
            t["makeTemplateHelpText"] = "Convert this pincer into a template to allow lots of separate events."
            t["startTime"] = "This event will start at:"
            t["confirmCandidate"] = "Confirm"
            t["open"] = "Open"
            t["pincerSettings"] = "Pincer Settings"
            t["notSet"] = "Not set"
            t["change-field"] = "Change %1%"
            t["summaries-summary"] = "%1% pincers"
            t["summaries-summary-one"] = "1 pincer"
            t["questions-summary"] = "%1% questions"
            t["questions-summary-one"] = "1 question"
            t["participants-summary"] = "%1% people"
            t["participants-summary-one"] = "1 person"
            t["suggestions-summary"] = "%1% suggested times"
            t["suggestions-summary-one"] = "1 suggested time"
            t["earlier"] = "Earlier messages"
            t["earlier-summary"] = "%1% earlier messages"
            t["earlier-summary-one"] = "1 earlier message"
        }

        init { defaults() }

        fun load(locale: String, log: Logger) {
            if (locale == loadedLocale) return
            log.i("Updating locale $loadedLocale -> $locale")
            loadedLocale = locale
            defaults()
            // TODO ... async network call (local cache?)
            if (locale.startsWith("en")) {
                // stick with default
            }
            else if (locale.startsWith("es")) {
                t["settings"] = "Configuración"
                t["es"] = "Español"
                t["fr"] = "Francés"
            }
            else if (locale.startsWith("fr")) {
                t["settings"] = "Configuration"
                t["es"] = "Espagnol"
                t["fr"] = "Français"
            }
            else {
                log.w("No tokens for language: $locale")
            }
        }

    }

}